import { defineComponent, ref, computed, onMounted, onUnmounted, watch, reactive } from 'vue';
import { MenuProps, NButton, NMenu } from 'naive-ui';
import { MenuMixedOption } from 'naive-ui/es/menu/src/interface';
import { RouteRecordRaw, useRouter } from 'vue-router';
import classNames from 'classnames';
import { debounce } from 'lodash-es';

import { routesMenusNew } from '@/router';
import { frontNavigationApi } from '@/api/modules/frontNavigation';
import SVG from '@dts/shared/src/assets/svg';
import styles from './index.module.scss';
import { PRIMARY_COLOR, PRIMARY_FONT_COLOR } from '@dts/shared/src/config/color';
import {
  buildFrontNavigationConfigMap,
  buildFrontNavigationMenuTree,
  hasNewBadge,
  type FrontNavigationMenuNode,
} from './navigationConfig';

export interface IMenuTitle {
  itemKey: string;
  isNew?: boolean;
  label?: string | Element;
  arrow?: boolean;
  onClick?: () => void;
  onMouseenter?: () => void;
  onMouseleave?: () => void;
}

const routeChildTraverse = (routeArray: RouteRecordRaw[] = []): string[] => {
  return routeArray
    ?.filter((r) => !r?.meta?.hide)
    .reduce((pre, route) => {
      route.name && pre.push(String(route.name));
      if (route?.children?.length) {
        return [...pre, ...routeChildTraverse(route.children)];
      }
      return pre;
    }, [] as string[]);
};

const HorizontalMenu = defineComponent({
  setup: () => {
    let inDropdownMenu = false;

    const activeKey = ref();
    const menuRef = ref();
    const dropdownRef = ref<Element | null>();

    const showDropdown = reactive<Record<string, boolean>>(
      routesMenusNew.children?.reduce((pre, r) => {
        return {
          ...pre,
          [String(r.name)]: false,
        };
      }, {}) as Record<string, boolean>,
    );

    const router = useRouter();
    const navigationConfigMap = ref(buildFrontNavigationConfigMap());

    const menuThemeOverrides: NonNullable<MenuProps['themeOverrides']> = {};

    const handleDirByName = (name: string) => {
      router.push({
        name,
      });
    };

    const flatRouter = routesMenusNew.children?.reduce(
      (pre, k) => ({
        ...pre,
        [String(k.name)]: routeChildTraverse(k.children),
      }),
      {},
    ) as Record<string, string[]>;

    const menuTree = computed(() => {
      return buildFrontNavigationMenuTree(routesMenusNew.children ?? [], navigationConfigMap.value);
    });

    const activeTab = (value: string) => {
      if (!value) return '';
      const keys = Object.keys(flatRouter) as (keyof typeof flatRouter)[];
      if (keys.includes(value)) return value;
      for (let i = 0; i < keys.length; i++) {
        if (flatRouter[keys[i]].includes(value)) {
          return keys[i];
        }
      }
      return '';
    };

    const activeLine = (key: string) => {
      return key === activeTab(activeKey.value) || showDropdown?.[key] ? styles.activeName : '';
    };

    const Title = ({ itemKey, isNew, label, onClick, arrow, onMouseenter, onMouseleave }: IMenuTitle) => {
      return (
        <div
          class={styles.menuItem}
          onClick={onClick}
          onMouseenter={() => onMouseenter && onMouseenter()}
          onMouseleave={() => onMouseleave && onMouseleave()}
        >
          <NButton text class={styles.menuTitle}>
            <div class={styles.titleCtxHide}>
              <div class={styles.name}>
                <div>{label}</div>
                {isNew && <SVG.HeaderNewTagsSVG />}
              </div>

              {Boolean(arrow) && (
                <div class={classNames(styles.arrow)}>
                  <SVG.HeaderArrow />
                </div>
              )}
            </div>
            <div class={styles.titleCtx}>
              <div
                class={classNames(styles.name, activeLine(itemKey))}
                style={{ color: showDropdown?.[itemKey] ? PRIMARY_COLOR : PRIMARY_FONT_COLOR }}
              >
                {label}
                {isNew && <SVG.HeaderNewTagsSVG />}
                <div
                  class={styles.after}
                  style={{
                    backgroundColor: showDropdown?.[itemKey] ? PRIMARY_COLOR : PRIMARY_FONT_COLOR,
                  }}
                />
              </div>
              {Boolean(arrow) && (
                <div
                  class={classNames(
                    styles.arrow,
                    showDropdown?.[itemKey] && styles.revertArrow,
                  )}
                >
                  <SVG.HeaderArrow />
                </div>
              )}
            </div>
          </NButton>
        </div>
      );
    };

    const fetchFrontNavigationConfig = async () => {
      try {
        const data = await frontNavigationApi.getFrontNavigationList();
        navigationConfigMap.value = buildFrontNavigationConfigMap(data);
      } catch (error) {
        console.error('fetchFrontNavigationConfig', error);
      }
    };

    const optionRender = (item: FrontNavigationMenuNode): MenuMixedOption => {
      return {
        label: () => (
          <div
            key={item.key}
            class={styles.subRoute}
            onClick={() => handleDirByName(item.key)}
            onKeypress={() => {}}
          >
            {item.label}
            {hasNewBadge(item) && <SVG.HeaderNewTagsSVG />}
          </div>
        ),
        key: item.key,
        children: item.children?.length
          ? item.children.map(optionRender)
          : undefined,
      };
    };
    const menuOptions = computed(() => {
      return menuTree.value.map((item) => {
        return {
          label: () => (
            <Title
              itemKey={item.key}
              label={item.label}
              isNew={hasNewBadge(item)}
              arrow={Boolean(item.children?.length)}
              onMouseenter={() => handleMouseEnter(item.key)}
              onMouseleave={() => handleMouseLeave()}
              onClick={() => handleDirByName(item.key)}
            />
          ),
          key: item.key,
          children: item.children?.length ? item.children.map(optionRender) : undefined,
        };
      }) as MenuMixedOption[];
    });

    const refreshShowDropdown = () => {
      Object.keys(showDropdown).forEach((k) => (showDropdown[k] = false));
    };

    const handleEnter = debounce((key: string) => {
      refreshShowDropdown();
      showDropdown?.[key] !== undefined && (showDropdown[key] = true);
    }, 100);

    const handleLeave = debounce(() => {
      !inDropdownMenu && refreshShowDropdown();
    }, 100);

    const handleMouseEnter = (key: string) => {
      handleEnter(key);
      inDropdownMenu = true;
      setTimeout(() => {
        dropdownRef.value = document.querySelector('.n-dropdown-menu');
      }, 100);
    };

    const handleMouseLeave = () => {
      inDropdownMenu = false;
      handleLeave();
    };

    const handleDropdownEnter = () => {
      const key = Object.keys(showDropdown).find((k) => showDropdown[k] === true);
      inDropdownMenu = true;
      key && handleEnter(key);
    };

    const handleDropdownLeave = () => {
      const key = Object.keys(showDropdown).find((k) => showDropdown[k] === true);
      inDropdownMenu = false;
      key && handleLeave();
    };

    watch(
      dropdownRef,
      (dom) => {
        dom?.addEventListener('mouseenter', handleDropdownEnter);
        dom?.addEventListener('mouseleave', handleDropdownLeave);
      },
      {
        immediate: true,
      },
    );

    watch(
      () => router.currentRoute.value.name,
      (name) => {
        activeKey.value = (router.currentRoute.value.meta?.activeMenu as string) || name;
      },
      {
        immediate: true,
        deep: true,
      },
    );

    onMounted(() => {
      fetchFrontNavigationConfig();
    });

    onUnmounted(() => {
      dropdownRef.value?.removeEventListener('mouseenter', handleDropdownEnter);
      dropdownRef.value?.removeEventListener('mouseleave', handleDropdownLeave);
    });

    return () => (
      <NMenu
        dropdownProps={{
          nodeProps: (op) => {
            const parentKey = activeTab(String(op.key ?? ''));
            handleMouseEnter(parentKey);
            return op as Record<string, string>;
          },
        }}
        ref={menuRef}
        mode="horizontal"
        themeOverrides={menuThemeOverrides}
        class={styles.horizontalMenu}
        v-model:value={activeKey.value}
        options={menuOptions.value}
      />
    );
  },
});

export default HorizontalMenu;
